/**
 * 3D working-area outline + custom-grid plane. Mirrors the 2D
 * PitchAreaOverlay so a half / quarter / custom view in 3D has the
 * same visible boundary the coach drew the drill within.
 *
 * - Full view: nothing rendered (the field markings already define the area)
 * - Half / quarter: dashed rectangle outline at y=0.02 above the pitch
 * - Custom: grass-textured plane substituted for the field + outline rectangle
 */
import { useMemo } from 'react';
import * as THREE from 'three';
import { visibleRegion, type PitchView, type CustomPitch } from '../pitchArea';
import type { PitchSpec } from '../pitchSpecs';

interface Props {
  spec: PitchSpec;
  view: PitchView;
  custom: CustomPitch | null;
}

const OUTLINE_Y = 0.02;
const OUTLINE_COLOR = '#27AE60';

export function DrillWorkingArea3D({ spec, view, custom }: Props): React.JSX.Element | null {
  if (view === 'full') return null;

  const region = visibleRegion(spec, view, custom);
  // In Josh's coord system: x = sideline (matches region.width axis),
  // z = goal-to-goal (matches region.length axis).
  const cx = region.cy; // sideline-centre
  const cz = region.cx; // goal-to-goal centre
  const halfW = region.width / 2;
  const halfL = region.length / 2;

  // Outline rectangle: 4 corners → closed line loop.
  const outlinePoints = useMemo(() => {
    const y = OUTLINE_Y;
    return [
      new THREE.Vector3(cx - halfW, y, cz - halfL),
      new THREE.Vector3(cx + halfW, y, cz - halfL),
      new THREE.Vector3(cx + halfW, y, cz + halfL),
      new THREE.Vector3(cx - halfW, y, cz + halfL),
      new THREE.Vector3(cx - halfW, y, cz - halfL),
    ];
  }, [cx, cz, halfW, halfL]);

  const outlineGeometry = useMemo(() => new THREE.BufferGeometry().setFromPoints(outlinePoints), [outlinePoints]);

  return (
    <group name="drill-working-area" userData={{ kind: 'drillWorkingArea', view }}>
      {/* For custom view, render a grass plane filling the working area
          since the full field is hidden by TacticsBoard3D's hideField. */}
      {view === 'custom' && custom && (
        <mesh position={[cx, 0.005, cz]} rotation={[-Math.PI / 2, 0, 0]} receiveShadow>
          <planeGeometry args={[region.width, region.length]} />
          <meshStandardMaterial color="#1d5a30" roughness={0.85} />
        </mesh>
      )}
      {/* Dashed outline. */}
      <lineSegments
        geometry={makeDashedRect(cx, cz, halfW, halfL, OUTLINE_Y)}
        userData={{ kind: 'drillWorkingAreaOutline' }}
      >
        <lineBasicMaterial color={OUTLINE_COLOR} transparent opacity={0.7} />
      </lineSegments>
      {/* Hide the constructed full line geometry — we use lineSegments above. */}
      <primitive object={outlineGeometry} attach="geometry" visible={false} />
    </group>
  );
}

/** Construct a "dashed" outline as line segments around a rectangle. */
function makeDashedRect(cx: number, cz: number, halfW: number, halfL: number, y: number): THREE.BufferGeometry {
  // Approximate dashes by emitting short segments around the perimeter.
  const perimeter = 4 * (halfW + halfL);
  const segLen = 0.6;
  const gap = 0.4;
  const period = segLen + gap;
  const count = Math.max(8, Math.floor(perimeter / period));
  const positions: number[] = [];

  // Build path point list (4 edges of the rectangle).
  const corners = [
    [cx - halfW, cz - halfL],
    [cx + halfW, cz - halfL],
    [cx + halfW, cz + halfL],
    [cx - halfW, cz + halfL],
  ];
  // Each edge as a polyline; sample dashes along its length.
  for (let i = 0; i < 4; i += 1) {
    const [x0, z0] = corners[i];
    const [x1, z1] = corners[(i + 1) % 4];
    const edgeLen = Math.hypot(x1 - x0, z1 - z0);
    const dashesPerEdge = Math.max(2, Math.floor(edgeLen / period));
    for (let d = 0; d < dashesPerEdge; d += 1) {
      const t0 = (d * period) / edgeLen;
      const t1 = Math.min(1, (d * period + segLen) / edgeLen);
      positions.push(
        x0 + (x1 - x0) * t0, y, z0 + (z1 - z0) * t0,
        x0 + (x1 - x0) * t1, y, z0 + (z1 - z0) * t1,
      );
    }
  }
  // Silence unused-var warning on `count` (kept for tuning).
  void count;

  const geom = new THREE.BufferGeometry();
  geom.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  return geom;
}
